/**
 * What we owe, to whom, and how late.
 *
 * Every figure is derived on each read, and the screen says so by showing the
 * ledger's own answer next to it. A subledger that disagrees with its control
 * account is the single most useful thing this page can tell somebody, so it
 * is at the top rather than buried in a close checklist.
 *
 * Two views of the same money: by vendor, aged; and bill by bill. Picking a
 * vendor in the first opens their bills in the second.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { ErrorNote, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { Tabs, matches, rowProps } from '../components/SidePanel'

interface PartyAgeing {
  party_id: string
  party_name: string
  buckets: Record<string, number>
  total_paise: number
  msme_clock: boolean
}

interface Ageing {
  as_at: string
  rows: PartyAgeing[]
  totals: Record<string, number>
  control_account_paise: number | null
  ties_out: boolean
}

interface OpenInvoice {
  invoice_id: string
  reference: string
  supplier_invoice_no: string
  party_id: string
  party_name: string
  invoice_date: string
  due_date: string
  total_paise: number
  paid_paise: number
  outstanding_paise: number
  days_overdue: number
  bucket: string
  msme_clock: boolean
}

const BUCKETS: { key: string; label: string }[] = [
  { key: 'not_due', label: 'Not due' },
  { key: '1_30', label: '1–30 days' },
  { key: '31_60', label: '31–60' },
  { key: '61_90', label: '61–90' },
  { key: 'over_90', label: 'Over 90' },
]

type Tab = 'vendors' | 'bills'

export default function Payables() {
  const [tab, setTab] = useState<Tab>('vendors')
  const [party, setParty] = useState('')
  const [search, setSearch] = useState('')
  const [onlyOverdue, setOnlyOverdue] = useState(false)

  const ageing = useQuery({
    queryKey: ['ageing'],
    queryFn: () => api.get<Ageing>('/payables/ageing'),
  })
  const invoices = useQuery({
    queryKey: ['open-invoices'],
    queryFn: () => api.get<OpenInvoice[]>('/payables/open-invoices'),
  })

  const data = ageing.data
  const vendors = (data?.rows ?? []).filter((r) => matches(search, r.party_name))
  const bills = (invoices.data ?? []).filter(
    (i) =>
      (!party || i.party_id === party) &&
      (!onlyOverdue || i.days_overdue > 0) &&
      matches(search, i.reference, i.supplier_invoice_no, i.party_name),
  )
  const pagedVendors = usePaged(vendors, search)
  const pagedBills = usePaged(bills, `${party}|${search}|${onlyOverdue}`)

  if (ageing.isLoading) return <Loading what="Working out what we owe" />
  const subledger = data ? Object.values(data.totals).reduce((a, b) => a + b, 0) : 0
  const overdue = data ? subledger - (data.totals.not_due ?? 0) : 0
  const allBills = invoices.data ?? []
  const msme = allBills.filter((i) => i.msme_clock && i.days_overdue > 0)
  const partyName = (data?.rows ?? []).find((r) => r.party_id === party)?.party_name

  return (
    <>
      <ErrorNote error={ageing.error ?? invoices.error} />

      {data && data.control_account_paise === null && (
        <Note kind="warn">
          No account is mapped to the payables control role, so the list below cannot be checked
          against the ledger. The figures are what the documents say. Map an account in the chart of
          accounts to close the loop.
        </Note>
      )}

      {data && data.control_account_paise !== null && !data.ties_out && (
        <Note kind="warn">
          The open bills come to {formatInr(subledger)} but the payables control account says{' '}
          {formatInr(data.control_account_paise)}. Journals posted straight to payables explain most
          of a difference like this. Anything left over means a payment is allocated to a bill it
          did not settle.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="We owe"
          value={formatInr(subledger)}
          note={`${allBills.length} open bill(s) · ${(data?.rows ?? []).length} vendor(s)`}
        />
        <Stat
          label="Past its due date"
          value={formatInr(overdue)}
          note={`${formatInr(data?.totals.over_90 ?? 0)} over 90 days`}
        />
        <Stat
          label="MSME bills overdue"
          value={String(msme.length)}
          note={
            msme.length
              ? `${formatInr(msme.reduce((t, i) => t + i.outstanding_paise, 0))} — interest accrues`
              : '45 days under s.15 — none past it'
          }
        />
        <Stat
          label="Against the ledger"
          value={
            !data || data.control_account_paise === null
              ? 'not checked'
              : data.ties_out
                ? 'agrees'
                : 'differs'
          }
          note={
            data && data.control_account_paise !== null
              ? `control account ${formatInr(data.control_account_paise)} · as at ${data.as_at}`
              : 'no control account mapped'
          }
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'vendors', label: 'By vendor, aged', count: (data?.rows ?? []).length },
              { key: 'bills', label: 'Open bills', count: allBills.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder={tab === 'vendors' ? 'Vendor' : 'Reference, supplier bill, vendor'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
            {tab === 'bills' && (
              <>
                <select
                  value={party}
                  onChange={(e) => setParty(e.target.value)}
                  aria-label="Vendor"
                >
                  <option value="">Every vendor</option>
                  {(data?.rows ?? []).map((r) => (
                    <option key={r.party_id} value={r.party_id}>
                      {r.party_name}
                    </option>
                  ))}
                </select>
                <label className="appr-check">
                  <input
                    type="checkbox"
                    checked={onlyOverdue}
                    onChange={(e) => setOnlyOverdue(e.target.checked)}
                  />
                  only overdue
                </label>
              </>
            )}
          </div>
        </header>

        <div className="body tight">
          {tab === 'vendors' &&
            (!data || data.rows.length === 0 ? (
              <div className="empty">Nothing outstanding.</div>
            ) : vendors.length === 0 ? (
              <div className="empty">No vendor matches that.</div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    {BUCKETS.map((b) => (
                      <th key={b.key} className="num">
                        {b.label}
                      </th>
                    ))}
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedVendors.rows.map((row) => (
                    <tr
                      key={row.party_id}
                      {...rowProps(() => {
                        setParty(row.party_id)
                        setSearch('')
                        setTab('bills')
                      }, false)}
                      title="Open this vendor’s bills"
                    >
                      <td>
                        <div className="strong">{row.party_name}</div>
                        {row.msme_clock && (
                          <div className="cell-sub">MSME — 45 days under s.15</div>
                        )}
                      </td>
                      {BUCKETS.map((b) => (
                        <td key={b.key} className="num">
                          {row.buckets[b.key] ? (
                            b.key === 'over_90' ? (
                              <strong style={{ color: 'var(--danger)' }}>
                                {formatInr(row.buckets[b.key])}
                              </strong>
                            ) : (
                              formatInr(row.buckets[b.key])
                            )
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                      ))}
                      <td className="num">
                        <strong>{formatInr(row.total_paise)}</strong>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <strong>Total{search ? ', every vendor' : ''}</strong>
                    </td>
                    {BUCKETS.map((b) => (
                      <td key={b.key} className="num">
                        <strong>{formatInr(data.totals[b.key] ?? 0)}</strong>
                      </td>
                    ))}
                    <td className="num">
                      <strong>{formatInr(subledger)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}

          {tab === 'bills' && (
            <>
              {party && (
                <div className="bank-result">
                  <Note kind="info">Showing {partyName ?? 'one vendor'}’s bills only.</Note>
                  <button className="quiet small" onClick={() => setParty('')}>
                    Show every vendor
                  </button>
                </div>
              )}
              {bills.length === 0 ? (
                <div className="empty">
                  {allBills.length === 0 ? 'Nothing outstanding.' : 'No bill matches that.'}
                </div>
              ) : (
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>Vendor and bill</th>
                      <th>Billed</th>
                      <th>Due</th>
                      <th className="num">Overdue</th>
                      <th className="num">Bill</th>
                      <th className="num">Paid</th>
                      <th className="num">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedBills.rows.map((row) => (
                      <tr key={row.invoice_id}>
                        <td>
                          <div className="strong">{row.party_name}</div>
                          <div className="cell-sub">
                            <span className="mono">{row.supplier_invoice_no}</span> ·{' '}
                            <span className="ref">{row.reference}</span>
                            {row.msme_clock && ' · MSME'}
                          </div>
                        </td>
                        <td className="muted date">{row.invoice_date}</td>
                        <td className="muted date">{row.due_date}</td>
                        <td className="num">
                          {row.days_overdue > 0 ? (
                            <span
                              className={`pill ${row.days_overdue > 90 ? 'overdue' : 'advisory'}`}
                            >
                              {row.days_overdue}d
                            </span>
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                        <td className="num muted">{formatInr(row.total_paise)}</td>
                        <td className="num muted">
                          {row.paid_paise ? (
                            formatInr(row.paid_paise)
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                        <td className="num">
                          <strong>{formatInr(row.outstanding_paise)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
        {tab === 'vendors' && (
          <Pager
            page={pagedVendors.page}
            pages={pagedVendors.pages}
            onPage={pagedVendors.setPage}
          />
        )}
        {tab === 'bills' && (
          <Pager page={pagedBills.page} pages={pagedBills.pages} onPage={pagedBills.setPage} />
        )}
      </section>
    </>
  )
}
